import CloseIcon from "@mui/icons-material/Close";
import { Box, Button, IconButton, Typography } from "@mui/material";
import { Modal } from "app/components/Modal";
import React from "react";
import { useInjectReducer, useInjectSaga } from "utils/redux-injectors";

import { addOrderModalSaga } from "../redux/saga";
import { reducer, sliceKey } from "../redux/slice";
import AddOrderField from "./AddOrderField";
import { useStyles } from "./styles";
import { OrderFieldConfig, useAddOrderModal } from "./useAddOrderModal";
import VehicleImageUpload from "./VehicleImageUpload";

type Props = {
  titleKey?: string;
};

const toNameOptions = (names: string[]) =>
  names
    .filter((name) => Boolean(name?.trim()))
    .map((name) => ({ value: name, label: name }));

const AddOrderModal = ({ titleKey = "QUOTES.MODAL.ADD_TITLE" }: Props) => {
  useInjectReducer({ key: sliceKey, reducer });
  useInjectSaga({ key: sliceKey, saga: addOrderModalSaga });

  const styles = useStyles();
  const modal = useAddOrderModal();

  const renderField = (field: OrderFieldConfig) => (
    <AddOrderField
      key={field.key}
      fieldKey={field.key}
      label={field.label}
      type={field.type}
      options={field.options}
      inputMode={field.inputMode}
      disabled={field.disabled}
      dropDownKey={field.dropDownKey}
      value={modal.form[field.key]}
      fieldLabel={modal.fieldLabel}
      placeholder={modal.placeholder}
      optionLabel={modal.optionLabel}
      onChange={modal.onFieldChange}
      onDropDownScroll={modal.onDropDownScroll}
      hasMoreDropDownItems={modal.hasMoreDropDownItems}
    />
  );

  return (
    <Modal open={modal.open} onClose={modal.onClose}>
      <Box className={styles.modal}>
        <Box className={styles.header}>
          <Typography className={styles.title}>{modal.t(titleKey)}</Typography>
          <IconButton
            className={styles.closeButton}
            onClick={modal.onClose}
            aria-label={modal.t("BUTTONS.CANCEL")}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box className={styles.body}>
          <Box className={styles.topRow}>
            <VehicleImageUpload
              form={modal.form}
              imageError={modal.imageError}
              fileInputRef={modal.fileInputRef}
              fieldLabel={modal.fieldLabel}
              uploadLabel={modal.uploadLabel}
              uploadHint={modal.uploadHint}
              cancelLabel={modal.cancelLabel}
              onOpenFilePicker={modal.openFilePicker}
              onClearImage={modal.clearImage}
              onFileChange={modal.onFileChange}
              onUploadKeyDown={modal.onUploadKeyDown}
            />

            <Box className={styles.selectStack}>
              {renderField({
                key: "customerName",
                label: "CUSTOMER_NAME",
                type: "select",
                dropDownKey: "customers",
                options: toNameOptions(modal.customerNames),
              })}
              {renderField({
                key: "subCustomer",
                label: "SUB_CUSTOMER",
                type: "select",
                options: modal.subCustomerOptions,
              })}
            </Box>
          </Box>

          {renderField({ key: "chassis", label: "CHASSIS", type: "text" })}
          {renderField({
            key: "manufacturer",
            label: "MANUFACTURER",
            type: "select",
            dropDownKey: "manufacturers",
            options: toNameOptions(modal.manufacturerNames),
          })}
          {renderField({
            key: "model",
            label: "MODEL",
            type: "select",
            dropDownKey: "models",
            options: toNameOptions(modal.modelNames),
          })}

          <Box className={styles.splitRow}>
            {modal.axleYearFields.map(renderField)}
          </Box>

          {renderField(modal.boxTypeField)}
        </Box>

        <Box className={styles.footer}>
          <Button
            className={styles.cancelButton}
            variant="outlined"
            onClick={modal.onClose}
          >
            {modal.t("BUTTONS.CANCEL")}
          </Button>
          <Button
            className={styles.addButton}
            variant="contained"
            disableElevation
            disabled={!modal.isValid}
            onClick={modal.onSubmit}
          >
            {modal.t("BUTTONS.ADD")}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default AddOrderModal;
